import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import '../styles/ConsumptionPage.css';

const ConsumptionPage = () => {
  return (
    <div className="consumption-page">
      <div className="page-header">
        <div className="container">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
          <h1 className="hero-medium">
            How to <span className="accent-orange">Consume</span> Cordyceps
          </h1>
          <p className="body-large page-intro">
            Understanding the different forms and how to integrate Cordyceps militaris into your wellness routine.
          </p>
        </div>
      </div>

      <div className="page-content section-padding">
        <div className="container">
          <div className="intro-section">
            <h2 className="heading-2">How Cordyceps Can Be Consumed</h2>
            <p className="body-large intro-text">
              Cordyceps militaris can be consumed in several forms, each offering unique advantages depending on convenience, absorption, and personal wellness goals. The effectiveness of Cordyceps depends not only on the mushroom itself, but also on how it is prepared and consumed.
            </p>
          </div>

          <div className="form-section">
            <div className="form-image-wrapper-full">
              <img 
                src="https://images.unsplash.com/photo-1630623093190-166d69e55f41" 
                alt="Dried Cordyceps militaris fruiting body"
                className="form-image-full"
              />
            </div>
            <div className="form-content-single">
              <div className="form-text">
                <h2 className="heading-2">Whole Fruiting Body</h2>
                
                <div className="form-subsection">
                  <h3 className="heading-3">What it is:</h3>
                  <p className="body-regular">
                    The fruiting body is the fully grown mushroom, harvested at maturity and dried naturally. This is the most complete and traditional form of Cordyceps.
                  </p>
                </div>

                <div className="form-subsection">
                  <h3 className="heading-3">Advantages:</h3>
                  <ul className="advantage-list">
                    <li>Contains the full spectrum of naturally occurring compounds</li>
                    <li>Closest to how Cordyceps exists in nature</li>
                    <li>Minimal processing</li>
                    <li>Preferred by purists and traditional wellness users</li>
                  </ul>
                </div>

                <div className="form-subsection">
                  <h3 className="heading-3">Best for:</h3>
                  <p className="body-regular best-for-text">
                    People who value whole-food wellness and minimal intervention.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="form-section alternate">
            <div className="form-content">
              <div className="form-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_adapt-wellness/artifacts/9ebjhaqj_WhatsApp%20Image%202026-01-29%20at%2013.30.37%20%281%29.jpeg" 
                  alt="Organic Cordyceps militaris powder"
                  className="form-image"
                />
              </div>
              <div className="form-text">
                <h2 className="heading-2">Cordyceps Powder</h2>
                
                <div className="form-subsection">
                  <h3 className="heading-3">What it is:</h3>
                  <p className="body-regular">
                    Cordyceps powder is made by finely grinding the dried fruiting body. It can be mixed into warm water, teas, smoothies, or food.
                  </p>
                </div>

                <div className="form-subsection">
                  <h3 className="heading-3">Advantages:</h3>
                  <ul className="advantage-list">
                    <li>Easy to consume and versatile</li>
                    <li>Allows consistent daily use</li>
                    <li>Retains the whole-mushroom profile</li>
                    <li>Suitable for modern wellness routines</li>
                  </ul>
                </div>

                <div className="form-subsection">
                  <h3 className="heading-3">Best for:</h3>
                  <p className="body-regular best-for-text">
                    People looking for convenience without compromising on whole-mushroom benefits.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-content">
              <div className="form-text">
                <h2 className="heading-2">Cordyceps Capsules</h2>
                
                <div className="form-subsection">
                  <h3 className="heading-3">What it is:</h3>
                  <p className="body-regular">
                    Encapsulated Cordyceps powder or extract in convenient, pre-measured doses. The most portable and travel-friendly option.
                  </p>
                </div>

                <div className="form-subsection">
                  <h3 className="heading-3">Advantages:</h3>
                  <ul className="advantage-list">
                    <li>Pre-measured for consistency</li>
                    <li>No taste or texture concerns</li>
                    <li>Ideal for busy lifestyles</li>
                    <li>Easy to integrate into daily routines</li>
                  </ul>
                </div>

                <div className="form-subsection">
                  <h3 className="heading-3">Best for:</h3>
                  <p className="body-regular best-for-text">
                    People who prioritize convenience and prefer traditional supplement formats.
                  </p>
                </div>
              </div>
              <div className="form-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_adapt-wellness/artifacts/7felaflm_Golden-Yellow-Capsules.jpg" 
                  alt="Cordyceps militaris capsules"
                  className="form-image"
                />
              </div>
            </div>
          </div>

          <div className="form-section alternate">
            <div className="form-content">
              <div className="form-image-wrapper">
                <img 
                  src="https://images.unsplash.com/photo-1690200191215-dbd85e2e4e0f" 
                  alt="Cordyceps tincture extract bottles"
                  className="form-image"
                />
              </div>
              <div className="form-text">
                <h2 className="heading-2">Cordyceps Tincture (Liquid Extract)</h2>
                
                <div className="form-subsection">
                  <h3 className="heading-3">What it is:</h3>
                  <p className="body-regular">
                    A tincture is a liquid extract made by soaking Cordyceps in a solvent to draw out soluble compounds.
                  </p>
                </div>

                <div className="form-subsection">
                  <h3 className="heading-3">Advantages:</h3>
                  <ul className="advantage-list">
                    <li>Quick absorption</li>
                    <li>Easy to add to water or take directly</li>
                    <li>Convenient for people who prefer liquid supplements</li>
                    <li>Portable and travel-friendly</li>
                  </ul>
                </div>

                <div className="form-subsection">
                  <h3 className="heading-3">Best for:</h3>
                  <p className="body-regular best-for-text">
                    Those who want fast, simple consumption with minimal preparation.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="comparison-section">
            <h2 className="heading-2">Choosing the Right Form</h2>
            <p className="body-large comparison-text">
              Each form of Cordyceps serves a different lifestyle. Whole fruiting bodies emphasize completeness, powders offer flexibility, capsules provide ultimate convenience, and tinctures enable fast absorption. The right choice depends on personal preference, routine, and how you integrate wellness into daily life.
            </p>
          </div>

          <div className="closing-section">
            <h2 className="heading-2">Mushhrushh's Approach</h2>
            <p className="body-large closing-text">
              At Mushhrushh, every form of Cordyceps begins with the same foundation — controlled cultivation, plant-based substrates, careful harvesting, and lab verification. Regardless of the format, quality starts at the source.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsumptionPage;